import { Box, IconButton, TextField, Typography } from "@mui/material";
import { grey } from "@mui/material/colors";
import {
  Outlet,
  createSearchParams,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { useBreakpoints } from "../../../hooks/useBreakpoins";
import { useEffect, useState } from "react";
import useHeader from "../../../hooks/context/useHeader";
import SearchIcon from "@mui/icons-material/Search";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import LocalPhoneIcon from "@mui/icons-material/LocalPhone";
import { LoginPopover } from "../../AtomicDesign/Organisms";
import { CustomBreadcrumbs, GlobalContainer } from "../../AtomicDesign/Atoms";
import { MenuDrawer } from "../MenuDrawer";
import { Logo } from "../Logo";
import useProducts from "../../../hooks/context/useProducts";
import { colors } from "../../../theme/colors";

// #2C3E50 blue 900
export function Header() {
  const { activeTab, setActiveTab } = useHeader();
  const { setProductsFilters } = useProducts();
  const nav = useNavigate();
  const [params, setSearchParams] = useSearchParams();
  const tabNow = params.get("tab");
  const isRareNow = params.get("isRare");
  const versionNow = params.get("versionName");
  const originNow = params.get("originName");
  const nameNow = params.get("name");
  const inStockNow = params.get("inStock");
  const outOfStockNow = params.get("outOfStock");
  const productTypeNow = params.get("productTypeName");

  const location = useLocation();
  const { semiSmalldesktop, isSmalldesktop, isMobile } = useBreakpoints();
  const [text, setText] = useState("");

  const showLeftMenu =
    location.pathname === "/products" || location.pathname === "/sale";

  const navigation = ({
    where,
    tab,
    isClick,
  }: {
    where?: string;
    tab?: number;
    isClick?: boolean;
  }) => {
    const tabs = tab !== undefined ? tab : showLeftMenu ? Number(tabNow) : 1;
    const loc = where ? where : showLeftMenu ? location.pathname : "/products";
    setActiveTab(tabs);

    const removeParams = !showLeftMenu
      ? createSearchParams({ tab: String(tabs) }).toString()
      : createSearchParams({
          ...(nameNow && {
            name: nameNow,
          }),
          ...(isClick &&
            text && {
              name: text.trimEnd(),
            }),
          ...(isRareNow && {
            isRare: isRareNow,
          }),
          ...(originNow && {
            originName: originNow,
          }),
          ...(versionNow && {
            versionName: versionNow,
          }),
          ...(inStockNow && {
            inStock: inStockNow,
          }),
          ...(outOfStockNow && {
            outOfStock: outOfStockNow,
          }),
          ...(productTypeNow && {
            productTypeName: productTypeNow,
          }),
          tab: String(tabs),
        }).toString();

    if (text) {
      setProductsFilters((old) => ({ ...old, name: text }));
    }

    nav({
      pathname: loc,
      search: removeParams,
    });
  };

  const cleanFilter = () => {
    setText("");
    setSearchParams(
      createSearchParams({
        tab: String(tabNow),
      }).toString()
    );
  };

  useEffect(() => {
    if (showLeftMenu) {
      setProductsFilters({
        offset: 0,
        itensPerPage: 18,
        name: nameNow !== "undefined" && nameNow !== "" ? nameNow : undefined,
        originName: originNow !== "undefined" ? originNow : undefined,
        versionName: versionNow !== "undefined" ? versionNow : undefined,
        inStock: inStockNow !== "undefined" ? inStockNow : undefined,
        outOfStock: outOfStockNow !== "undefined" ? outOfStockNow : undefined,
        isRare: isRareNow !== "undefined" ? isRareNow : undefined,
        productTypeName:
          productTypeNow !== "undefined" ? productTypeNow : undefined,
        ...(location.pathname === "/sale" && { isSale: true }),
      });
      if (nameNow) {
        setText(nameNow);
      }
    }

    // quando a pagina recarrega, bota os valores de novo nos campos
    // eslint-disable-next-line
  }, [params]);

  useEffect(() => {
    if (showLeftMenu) {
      setSearchParams(
        createSearchParams({
          ...(nameNow && {
            name: nameNow,
          }),
          ...(isRareNow && {
            isRare: isRareNow,
          }),
          ...(originNow && {
            originName: originNow,
          }),
          ...(versionNow && {
            versionName: versionNow,
          }),
          ...(inStockNow && {
            inStock: inStockNow,
          }),
          ...(outOfStockNow && {
            outOfStock: outOfStockNow,
          }),
          ...(productTypeNow && {
            productTypeName: productTypeNow,
          }),
        }).toString()
      );

      navigation({ isClick: true });
    } else {
      if (tabNow) {
        setSearchParams(createSearchParams({ tab: String(tabNow) }).toString());
      }
    }

    if (tabNow) {
      setActiveTab(Number(tabNow));
    } else {
      setActiveTab(0);
    }
    // eslint-disable-next-line
  }, [showLeftMenu]);

  useEffect(() => {
    const searchText = document?.getElementById("textfield-search");

    searchText?.addEventListener("keypress", (e) => {
      if (e.key === "Enter") {
        document?.getElementById("confirm-button")?.click();
      }
    });

    // eslint-disable-next-line
  }, []);

  const typographyCss = (selected) => {
    const defaultCss = {
      cursor: "pointer",
      p: 1,
      borderRadius: 2,
      "&:hover": {
        background: colors.purpleAlpha[800],
        color: "#FFFFFF",
      },
    };
    if (selected === tabNow) {
      return {
        ...defaultCss,
        borderRadius: 0,
        borderTop: `1px solid ${colors.purple[500]}`,
        borderBottom: `1px solid ${colors.purple[500]}`,
      };
    } else {
      return defaultCss;
    }
  };

  const crumbsList = () => {
    switch (location.pathname) {
      case "/products":
        return [
          { crumbName: "Início", path: "/" },
          { crumbName: "Produtos", path: "/products?tab=1" },
        ];
      case "/contact":
        return [
          { crumbName: "Início", path: "/" },
          { crumbName: "Sobre nós", path: "/contact?tab=3" },
        ];
      default:
        return [];
    }
  };

  return (
    <Box>
      <Box
        sx={{
          background: grey["A100"],
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          width: "100%",
          gap: 4,
          height: semiSmalldesktop ? 80 : 92,
        }}
      >
        <Typography
          color={colors.purple[500]}
          fontSize={semiSmalldesktop ? 12 : 16}
          display={"flex"}
          flexWrap={"wrap"}
          justifyContent={"center"}
          alignItems={"center"}
        >
          <LocalPhoneIcon sx={{ mr: 1 }} /> +55 (16) 99461-9371
        </Typography>
        <Typography
          color={colors.purple[500]}
          fontSize={semiSmalldesktop ? 12 : 16}
          display={"flex"}
          flexWrap={"wrap"}
          justifyContent={"center"}
          alignItems={"center"}
        >
          <MailOutlineIcon sx={{ mr: 1 }} />
          email@coelhomagico.com.br
        </Typography>
      </Box>
      <Box width={"100%"}>
        <GlobalContainer sx={{ p: 0 }}>
          <Box
            p={semiSmalldesktop ? "2px 15px" : 2}
            display={"flex"}
            justifyContent={"space-between"}
            alignItems={isSmalldesktop ? "flex-start" : "center"}
            sx={{ boxShadow: "0px 15px 10px -20px #000000" }}
          >
            <Box
              display={"flex"}
              alignItems={"center"}
              justifyContent={"flex-start"}
              flexWrap={"wrap"}
              width={"100%"}
              gap={isSmalldesktop ? 1 : 20}
            >
              <Box
                display={"flex"}
                alignItems={"center"}
                justifyContent={isSmalldesktop ? "space-around" : "center"}
                width={isMobile ? "100%" : "auto"}
              >
                <MenuDrawer
                  navigation={(where, tab) => navigation({ where, tab })}
                  activeTab={activeTab}
                />

                <Logo onClick={() => navigation({ where: "/home", tab: 0 })} />
                {isMobile && <LoginPopover setTab={() => setActiveTab(3)} />}
              </Box>

              <Box
                display={"flex"}
                justifyContent={"center"}
                gap={3}
                width={isSmalldesktop ? "100%" : "auto"}
                sx={{ ml: isSmalldesktop && !isMobile ? 20 : 0 }}
              >
                <Typography
                  sx={typographyCss("0")}
                  onClick={() => navigation({ where: "/home", tab: 0 })}
                  color={colors.purple[500]}
                >
                  Início
                </Typography>
                <Typography
                  sx={typographyCss("1")}
                  onClick={() => navigation({ where: "/products", tab: 1 })}
                  color={colors.purple[500]}
                >
                  Produtos
                </Typography>
                <Typography
                  sx={typographyCss("3")}
                  onClick={() => navigation({ where: "/contact", tab: 3 })}
                  color={colors.purple[500]}
                >
                  Sobre nós
                </Typography>
              </Box>
            </Box>

            {!semiSmalldesktop ? (
              <Box
                display={"flex"}
                alignItems={"center"}
                gap={10}
                pr={3}
                mt={isSmalldesktop ? 5 : 0}
              >
                <TextField
                  id="textfield-search"
                  sx={{
                    minWidth: 220,
                    background: "#F5F5F510",
                    border: "none",
                  }}
                  label="PESQUISA"
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  InputLabelProps={{ style: { color: "#D6D6D6" } }}
                  focused
                  variant="filled"
                  InputProps={{
                    disableUnderline: true,
                    endAdornment: (
                      <>
                        {text && (
                          <IconButton
                            onClick={cleanFilter}
                            style={{
                              marginRight: 5,
                              fontSize: 13,
                              width: 31,
                              color: colors.purple[500],
                            }}
                          >
                            X
                          </IconButton>
                        )}
                        <IconButton
                          id="confirm-button"
                          onClick={(e) => {
                            navigation({ isClick: true });
                          }}
                          style={{
                            marginRight: 5,
                            fontSize: 13,
                            width: 31,
                            color: colors.purple[500],
                          }}
                        >
                          <SearchIcon />
                        </IconButton>
                      </>
                    ),
                  }}
                />

                <LoginPopover setTab={() => setActiveTab(3)} />
              </Box>
            ) : !isMobile ? (
              <LoginPopover setTab={() => setActiveTab(3)} />
            ) : (
              <></>
            )}
          </Box>
        </GlobalContainer>
      </Box>

      <GlobalContainer sx={{ p: 0 }}>
        {semiSmalldesktop && (
          <Box width={"100%"} display={"flex"} justifyContent={"center"} mt={2}>
            <TextField
              id="textfield-search"
              sx={{
                width: semiSmalldesktop ? "95%" : "auto",
                minWidth: 220,
                background: "#F5F5F510",
                border: "none",
              }}
              label="PESQUISA"
              value={text}
              onChange={(e) => setText(e.target.value)}
              InputLabelProps={{ style: { color: "#D6D6D6" } }}
              focused
              variant="filled"
              InputProps={{
                disableUnderline: true,
                endAdornment: (
                  <>
                    {text && (
                      <IconButton
                        onClick={cleanFilter}
                        style={{
                          marginRight: 5,
                          fontSize: 13,
                          width: 31,
                          color: colors.purple[500],
                        }}
                      >
                        X
                      </IconButton>
                    )}
                    <IconButton
                      id="confirm-button"
                      onClick={(e) => {
                        navigation({ isClick: true });
                      }}
                      style={{
                        marginRight: 5,
                        fontSize: 13,
                        width: 31,
                        color: colors.purple[500],
                      }}
                    >
                      <SearchIcon />
                    </IconButton>
                  </>
                ),
              }}
            />
          </Box>
        )}
        <CustomBreadcrumbs crumbs={crumbsList()} />
        <Box
          display={"flex"}
          flexDirection={semiSmalldesktop ? "column" : "row"}
        >
          <Outlet />
          {/* <WarningMessage /> */}
        </Box>
      </GlobalContainer>
    </Box>
  );
}
